import { uniq } from 'lodash';

import api from 'core/util/api';
import DeviceCollection from './DeviceCollection';
import DeviceModel from './DeviceModel';

const DEVICE_LOOKUP_LIMIT = 500;

interface DeviceLookup {
  ids?: Array<string | number | null>;
  names?: Array<string | null>;
}

interface DeviceLookupResponse {
  id: string | number;
  [key: string]: unknown;
}

const normalizeLookup = ({ ids = [], names = [] }: DeviceLookup = {}) => ({
  ids: uniq(ids.filter((id) => /^\d+$/.test(String(id))).map(String)),
  names: uniq(names.filter((name): name is string => typeof name === 'string' && name.length > 0))
});

export default class DeviceCacheCollection extends DeviceCollection {
  private readonly fetchesByKey = new Map<string, Promise<void>>();

  constructor(private readonly getScopeGeneration: () => number) {
    super();
  }

  byId(id: string | number | null | undefined): DeviceModel | undefined {
    return id == null ? undefined : this.get(String(id));
  }

  byName(name: string | null | undefined): DeviceModel | undefined {
    const id = name ? this.deviceNameIdHash[name] : undefined;
    return id == null ? undefined : this.get(id);
  }

  ensure({ ids = [], names = [] }: DeviceLookup = {}): Promise<void> {
    const normalized = normalizeLookup({ ids, names });
    if (normalized.ids.length + normalized.names.length > DEVICE_LOOKUP_LIMIT) {
      return Promise.reject(new Error(`Look up to ${DEVICE_LOOKUP_LIMIT} devices at a time`));
    }

    const uncachedIds = normalized.ids.filter((id) => !this.byId(id));
    const uncachedNames = normalized.names.filter((name) => !this.byName(name));
    if (!uncachedIds.length && !uncachedNames.length) {
      return Promise.resolve();
    }

    const scopeGeneration = this.getScopeGeneration();
    const key = `${scopeGeneration}:${JSON.stringify({ ids: uncachedIds.sort(), names: uncachedNames.sort() })}`;
    const existingFetch = this.fetchesByKey.get(key);
    if (existingFetch) {
      return existingFetch;
    }

    const fetch = api
      .post('/api/ui/devices/find', { body: { ids: uncachedIds, names: uncachedNames } })
      .then((devices: DeviceLookupResponse[]) => {
        if (this.getScopeGeneration() !== scopeGeneration) {
          return;
        }

        const newDevices: DeviceLookupResponse[] = [];
        let updatedExistingDevice = false;
        devices.forEach((attributes) => {
          const existing = this.byId(attributes.id);
          if (existing) {
            existing.set(existing.deserialize(attributes));
            updatedExistingDevice = true;
          } else {
            newDevices.push(attributes);
          }
        });

        if (newDevices.length) {
          this.add(newDevices);
        } else if (updatedExistingDevice) {
          this.setLastUpdated();
        }
      })
      .finally(() => this.fetchesByKey.delete(key));
    this.fetchesByKey.set(key, fetch);
    return fetch;
  }

  fetchById(id: string | number): Promise<DeviceModel | undefined> {
    return this.ensure({ ids: [id] }).then(() => this.byId(id));
  }

  fetchByName(name: string): Promise<DeviceModel | undefined> {
    return this.ensure({ names: [name] }).then(() => this.byName(name));
  }
}
